import { useState, useEffect } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { useToast } from '../../components/Toast'
import { useAuth } from '../../store/AuthContext'
import attitudesService from '../../services/attitudesService'
import scoresService from '../../services/scoresService'
import employeesService from '../../services/employeesService'
import { AttitudeCategory, Employee, CheckExistingResponse } from '../../services/types'
import { SearchableSelect } from '../../components/SearchableSelect'
import { Tabs } from '../../components/Tabs'
import { AttitudeCard } from '../../components/AttitudeCard'
import { ConfirmModal } from '../../components/ConfirmModal'

const MONTH_NAMES = [
  'Janeiro', 'Fevereiro', 'Março', 'Abril', 'Maio', 'Junho',
  'Julho', 'Agosto', 'Setembro', 'Outubro', 'Novembro', 'Dezembro',
]

export const AssessmentPage = () => {
  const toast = useToast()
  const { user } = useAuth()
  const queryClient = useQueryClient()
  const isAdmin = user?.role === 'admin'

  // Self-assessment setting
  const { data: selfAssessmentSetting } = useQuery({
    queryKey: ['self-assessment-setting'],
    queryFn: () => scoresService.getSelfAssessmentSetting(),
    enabled: isAdmin,
  })

  const toggleSelfAssessmentMutation = useMutation({
    mutationFn: (enabled: boolean) => scoresService.setSelfAssessmentSetting(enabled),
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ['self-assessment-setting'] })
      toast.success(
        data.is_enabled
          ? 'Autoavaliação habilitada para funcionários.'
          : 'Autoavaliação desabilitada.'
      )
    },
    onError: () => {
      toast.error('Erro ao alterar configuração.')
    },
  })

  // Current month/year
  const now = new Date()
  const currentMonth = now.getMonth() + 1 // 1-12
  const currentYear = now.getFullYear()

  // State
  const [activeTab, setActiveTab] = useState<'positive' | 'negative'>('positive')
  const [selectedEmployee, setSelectedEmployee] = useState<number | null>(null)
  const [selectedMonth, setSelectedMonth] = useState<number>(currentMonth)
  const [selectedYear, setSelectedYear] = useState<number>(currentYear)
  const [selectedAttitudes, setSelectedAttitudes] = useState<Set<number>>(new Set())
  const [observation, setObservation] = useState('')
  const [showAntifoundationModal, setShowAntifoundationModal] = useState(false)
  const [antifoundationId, setAntifoundationId] = useState<number | null>(null)
  const [existingScore, setExistingScore] = useState<CheckExistingResponse | null>(null)
  const [isCheckingExisting, setIsCheckingExisting] = useState(false)
  const [editingScoreId, setEditingScoreId] = useState<number | null>(null)
  const [isLoadingScore, setIsLoadingScore] = useState(false)

  // Fetch attitudes
  const { data: attitudes = [], isLoading: isLoadingAttitudes } = useQuery({
    queryKey: ['attitudes'],
    queryFn: () => attitudesService.getAll(),
  })

  // Fetch employees
  const { data: employees = [], isLoading: isLoadingEmployees } = useQuery({
    queryKey: ['employees'],
    queryFn: () => employeesService.getAll({ is_active: true, is_ownership_eligible: true }),
  })

  // Check existing score when employee + month + year change
  useEffect(() => {
    if (!selectedEmployee) {
      setExistingScore(null)
      setEditingScoreId(null)
      setSelectedAttitudes(new Set())
      setObservation('')
      return
    }

    setIsCheckingExisting(true)
    setEditingScoreId(null)
    scoresService
      .checkExisting(selectedEmployee, selectedMonth, selectedYear)
      .then(async (result) => {
        setExistingScore(result)
        if (result.exists && !result.is_reversed && result.score_id) {
          // Load existing score data for editing (includes self-assessments)
          setIsLoadingScore(true)
          try {
            const score = await scoresService.getById(result.score_id)
            if (score.selected_attitudes && score.selected_attitudes.length > 0) {
              setSelectedAttitudes(new Set(score.selected_attitudes))
            }
            setObservation(score.observation || '')
            setEditingScoreId(score.id)
          } catch (err) {
            console.error('Failed to load existing score:', err)
          } finally {
            setIsLoadingScore(false)
          }
        } else {
          setSelectedAttitudes(new Set())
          setObservation('')
        }
      })
      .catch(() => setExistingScore(null))
      .finally(() => setIsCheckingExisting(false))
  }, [selectedEmployee, selectedMonth, selectedYear])

  // Separate attitudes by type
  const positiveAttitudes = attitudes
    .filter((a) => a.type === 'positive')
    .sort((a, b) => a.display_order - b.display_order)

  const negativeAttitudes = attitudes
    .filter((a) => a.type === 'negative')
    .sort((a, b) => a.display_order - b.display_order)

  // Calculate points
  const calculatePoints = (): number => {
    const selected = Array.from(selectedAttitudes)
      .map((id) => attitudes.find((a) => a.id === id))
      .filter(Boolean) as AttitudeCategory[]

    // Highest positive wins
    const positives = selected.filter((a) => a.type === 'positive')
    const highestPositive = positives.length > 0
      ? Math.max(...positives.map((a) => a.points))
      : 0

    // All negatives subtract
    const negatives = selected.filter((a) => a.type === 'negative')
    const totalNegative = negatives.reduce((sum, a) => sum + a.points, 0)

    return highestPositive + totalNegative
  }

  // Create score mutation
  const createScoreMutation = useMutation({
    mutationFn: scoresService.create,
    onSuccess: () => {
      setSelectedAttitudes(new Set())
      setObservation('')
      setSelectedEmployee(null)
      setEditingScoreId(null)
      toast.success('Avaliação registrada com sucesso!')
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao registrar avaliação.'
      toast.error(message)
    },
  })

  // Update score mutation
  const updateScoreMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<import('../../services/types').CreateScoreRequest> }) =>
      scoresService.update(id, data),
    onSuccess: () => {
      setSelectedAttitudes(new Set())
      setObservation('')
      setSelectedEmployee(null)
      setEditingScoreId(null)
      toast.success('Avaliação atualizada com sucesso!')
    },
    onError: (error: any) => {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        'Erro ao atualizar avaliação.'
      toast.error(message)
    },
  })

  // Toggle attitude selection
  const toggleAttitude = (id: number) => {
    const attitude = attitudes.find((a) => a.id === id)
    if (!attitude) return

    const newSelected = new Set(selectedAttitudes)

    // If already selected, deselect
    if (newSelected.has(id)) {
      newSelected.delete(id)
      setSelectedAttitudes(newSelected)
      return
    }

    // Check if it's Antifoundation
    if (attitude.is_special_antifoundation) {
      setAntifoundationId(id)
      setShowAntifoundationModal(true)
      return
    }

    // Add to selection
    newSelected.add(id)
    setSelectedAttitudes(newSelected)
  }

  // Confirm Antifoundation selection
  const confirmAntifoundation = () => {
    if (antifoundationId) {
      const newSelected = new Set(selectedAttitudes)
      newSelected.add(antifoundationId)
      setSelectedAttitudes(newSelected)
    }
    setShowAntifoundationModal(false)
    setAntifoundationId(null)
  }

  // Submit assessment
  const handleSubmit = () => {
    if (!selectedEmployee) {
      toast.warning('Selecione um funcionário.')
      return
    }

    if (selectedAttitudes.size === 0) {
      toast.warning('Selecione ao menos uma atitude.')
      return
    }

    if (editingScoreId) {
      updateScoreMutation.mutate({
        id: editingScoreId,
        data: {
          selected_attitudes: Array.from(selectedAttitudes),
          observation: observation.trim() || undefined,
        },
      })
    } else {
      createScoreMutation.mutate({
        employee: selectedEmployee,
        month: selectedMonth,
        year: selectedYear,
        selected_attitudes: Array.from(selectedAttitudes),
        observation: observation.trim() || undefined,
      })
    }
  }

  const totalPoints = calculatePoints()
  const isEditing = !!editingScoreId
  const isSaving = createScoreMutation.isPending || updateScoreMutation.isPending

  return (
    <div>
      {/* Header */}
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-2">
            Avaliar Atitudes
          </h1>
          <p className="text-gray-600 dark:text-gray-400">
            Avaliação mensal de {MONTH_NAMES[selectedMonth - 1]} de {selectedYear}
          </p>
        </div>

        {/* Self-assessment toggle (admin only) */}
        {isAdmin && selfAssessmentSetting && (
          <div className="flex items-center gap-3 bg-white dark:bg-gray-800 rounded-card shadow px-4 py-3">
            <span className="text-sm font-medium text-gray-700 dark:text-gray-300 whitespace-nowrap">
              Autoavaliação
            </span>
            <button
              onClick={() => toggleSelfAssessmentMutation.mutate(!selfAssessmentSetting.is_enabled)}
              disabled={toggleSelfAssessmentMutation.isPending}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 dark:focus:ring-offset-gray-800 cursor-pointer ${
                selfAssessmentSetting.is_enabled
                  ? 'bg-green-500'
                  : 'bg-gray-300 dark:bg-gray-600'
              }`}
              title={selfAssessmentSetting.is_enabled ? 'Habilitada — clique para desabilitar' : 'Desabilitada — clique para habilitar'}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${
                  selfAssessmentSetting.is_enabled ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>
        )}
      </div>

      {/* Employee + Month/Year Select */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {/* Employee */}
          <div className="md:col-span-1">
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Funcionário *
            </label>
            <SearchableSelect<Employee>
              options={employees}
              value={employees.find((e) => e.id === selectedEmployee) ?? null}
              onChange={(emp) => setSelectedEmployee(emp ? emp.id : null)}
              getLabel={(e) => e.full_name}
              getSubLabel={(e) => e.primary_company_detail.name}
              getId={(e) => e.id}
              placeholder="Buscar funcionário..."
              disabled={isLoadingEmployees}
              isLoading={isLoadingEmployees}
            />
          </div>

          {/* Month */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Mês *
            </label>
            <select
              value={selectedMonth}
              onChange={(e) => setSelectedMonth(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              {MONTH_NAMES.map((name, index) => (
                <option key={index + 1} value={index + 1}>
                  {name}
                </option>
              ))}
            </select>
          </div>

          {/* Year */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Ano *
            </label>
            <select
              value={selectedYear}
              onChange={(e) => setSelectedYear(Number(e.target.value))}
              className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent"
            >
              <option value={currentYear}>{currentYear}</option>
              <option value={currentYear - 1}>{currentYear - 1}</option>
            </select>
          </div>
        </div>
      </div>

      {/* Existing Score Warning */}
      {isCheckingExisting && selectedEmployee && (
        <div className="bg-gray-100 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-card p-4 mb-6">
          <p className="text-sm text-gray-600 dark:text-gray-400">Verificando avaliações existentes...</p>
        </div>
      )}

      {existingScore?.exists && (
        <div className={`border rounded-card p-4 mb-6 ${
          existingScore.is_reversed
            ? 'bg-blue-50 dark:bg-blue-900/20 border-blue-300 dark:border-blue-700'
            : 'bg-primary/10 border-primary/30'
        }`}>
          <div className="flex items-start gap-3">
            <span className="text-2xl">{existingScore.is_reversed ? 'ℹ️' : '✏️'}</span>
            <div>
              <p className={`font-semibold ${
                existingScore.is_reversed
                  ? 'text-blue-800 dark:text-blue-300'
                  : 'text-primary'
              }`}>
                {existingScore.is_reversed
                  ? 'Existe uma avaliação revertida para este período'
                  : 'Editando avaliação existente'}
              </p>
              <p className={`text-sm mt-1 ${
                existingScore.is_reversed
                  ? 'text-blue-700 dark:text-blue-400'
                  : 'text-gray-600 dark:text-gray-400'
              }`}>
                {existingScore.is_reversed
                  ? `A avaliação anterior (${existingScore.points_snapshot} pontos) foi revertida. Você pode criar uma nova avaliação.`
                  : isLoadingScore
                  ? 'Carregando avaliação...'
                  : `Avaliação de ${MONTH_NAMES[selectedMonth - 1]}/${selectedYear} com ${existingScore.points_snapshot} pontos. Altere as atitudes e salve para atualizar.`
                }
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Points Summary */}
      <div className="bg-primary/10 border border-primary/20 rounded-card p-6 mb-6">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">
              Pontuação Calculada
            </p>
            <p className="text-3xl font-bold text-gray-900 dark:text-white">
              {totalPoints > 0 ? '+' : ''}{totalPoints} pontos
            </p>
          </div>
          <div className="text-4xl">
            {totalPoints > 0 ? '✨' : totalPoints < 0 ? '⚠️' : '➖'}
          </div>
        </div>
        <p className="text-xs text-gray-500 dark:text-gray-500 mt-2">
          Maior atitude positiva + soma de negativas
        </p>
      </div>

      {/* Tabs */}
      <div className="mb-6">
        <Tabs
          tabs={[
            { id: 'positive', label: 'Positivo', icon: '✓' },
            { id: 'negative', label: 'Negativo', icon: '✗' },
          ]}
          activeTab={activeTab}
          onTabChange={(tab) => setActiveTab(tab as 'positive' | 'negative')}
        />
      </div>

      {/* Attitudes Grid */}
      {isLoadingAttitudes ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-gray-600 dark:text-gray-400">Carregando atitudes...</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          {activeTab === 'positive'
            ? positiveAttitudes.map((attitude) => (
                <AttitudeCard
                  key={attitude.id}
                  attitude={attitude}
                  isSelected={selectedAttitudes.has(attitude.id)}
                  onToggle={toggleAttitude}
                />
              ))
            : negativeAttitudes.map((attitude) => (
                <AttitudeCard
                  key={attitude.id}
                  attitude={attitude}
                  isSelected={selectedAttitudes.has(attitude.id)}
                  onToggle={toggleAttitude}
                />
              ))}
        </div>
      )}

      {/* Observation */}
      <div className="bg-white dark:bg-gray-800 rounded-card shadow p-6 mb-6">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
          Observações (opcional)
        </label>
        <textarea
          value={observation}
          onChange={(e) => setObservation(e.target.value)}
          rows={4}
          className="w-full px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-btn bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent resize-none"
          placeholder="Adicione contexto ou justificativa para esta avaliação..."
        />
      </div>

      {/* Submit Button */}
      <div className="flex justify-end">
        <button
          onClick={handleSubmit}
          disabled={
            isSaving ||
            isLoadingScore ||
            !selectedEmployee ||
            selectedAttitudes.size === 0
          }
          className="px-8 py-3 bg-primary hover:bg-primary-hover text-white font-medium rounded-btn transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
        >
          {isSaving
            ? (isEditing ? 'Salvando...' : 'Registrando...')
            : (isEditing ? 'Salvar Alterações' : 'Registrar Avaliação')}
        </button>
      </div>

      {/* Antifoundation Modal */}
      <ConfirmModal
        isOpen={showAntifoundationModal}
        onClose={() => {
          setShowAntifoundationModal(false)
          setAntifoundationId(null)
        }}
        onConfirm={confirmAntifoundation}
        title="⚠️ Confirmar Antifundador"
        description={
          <div className="space-y-3">
            <p className="font-semibold text-danger">
              Esta é uma ação irreversível e crítica!
            </p>
            <p>
              Ao selecionar <strong>Antifundador</strong>, as seguintes consequências serão aplicadas automaticamente:
            </p>
            <ul className="list-disc list-inside space-y-1 text-sm">
              <li>Todos os pontos do trimestre corrente serão zerados</li>
              <li>O funcionário será marcado como inelegível para Ownership</li>
              <li>Esta ação será registrada no log de auditoria</li>
            </ul>
            <p className="text-sm font-medium">
              Deseja realmente continuar?
            </p>
          </div>
        }
        confirmText="Sim, Confirmar"
        cancelText="Cancelar"
        isDanger={true}
      />
    </div>
  )
}

export default AssessmentPage
